import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Minimize2, Sparkles, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import api from '@/services/api';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const FloatingAIChat = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const messagesEndRef = useRef(null);

  // Check authentication status
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    setIsAuthenticated(!!token);
  }, [location]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Get context from current page
  const getPageContext = () => {
    const path = location.pathname;
    const contexts = {
      '/dashboard': 'Command Center - Overview metrics and KPIs',
      '/bom': 'BOM Intelligence - Bill of Materials management',
      '/import': 'Import Studio - BOM file upload',
      '/savings': 'Savings Intelligence - Comprehensive savings analysis',
      '/simulator': 'Cost Architect - AI-powered price simulation',
      '/predictions': 'Market Pulse - Price predictions and trends',
      '/risks': 'Risk Radar - Risk analysis and mitigation',
      '/distributors': 'Supplier Scorecard - Distributor performance',
    };
    return contexts[path] || 'General assistance';
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const contextInfo = `User is currently on: ${getPageContext()}. `;
      const response = await api.post('/ai/chat', { 
        message: contextInfo + userMessage 
      });
      
      // Add AI response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'error', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Don't render on login page or when not authenticated
  if (location.pathname === '/login' || location.pathname === '/auth/callback' || !isAuthenticated) {
    return null;
  }

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-12 w-12 rounded-full bg-gray-800 hover:bg-gray-700 shadow-lg z-50 transition-all duration-200"
        data-testid="open-ai-chat"
      >
        <Sparkles className="h-5 w-5 text-white" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-96 h-[600px] shadow-2xl z-50 bg-white border-gray-200 flex flex-col rounded-2xl overflow-hidden" data-testid="ai-chat-widget">
      <CardHeader className="bg-gradient-to-r from-green-600 via-emerald-500 to-teal-600 text-white flex-shrink-0 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
              <Bot className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                1AAgent
                <Sparkles className="h-4 w-4 text-yellow-300" />
              </CardTitle>
              <p className="text-xs text-white/80 mt-0.5">
                AI-Powered Supply Chain Intelligence
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 h-8 w-8 rounded-full"
              data-testid="minimize-ai-chat"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 h-8 w-8 rounded-full"
              data-testid="close-ai-chat"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-green-300 animate-pulse"></div>
          <p className="text-xs text-white/90">
            {getPageContext().split(' - ')[0]}
          </p>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center mb-4">
                <Bot className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-gray-800 font-semibold mb-2 text-lg">{t('ai.askAnything', 'Ask 1AAgent Anything!')}</p>
              <p className="text-sm text-gray-500 px-4 leading-relaxed">
                {t('ai.helpText', 'I can help explain calculations, analyze your data, and answer questions about supply chain optimization.')}
              </p>
              <div className="mt-6 grid grid-cols-1 gap-2 px-4">
                <button 
                  onClick={() => setInput('Explain how savings are calculated')}
                  className="text-left text-sm p-3 rounded-xl bg-gray-50 hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200"
                >
                  <Sparkles className="h-3 w-3 inline mr-2 text-green-500" />
                  Explain how savings are calculated
                </button>
                <button 
                  onClick={() => setInput('What risks should I focus on?')}
                  className="text-left text-sm p-3 rounded-xl bg-gray-50 hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200"
                >
                  <Sparkles className="h-3 w-3 inline mr-2 text-green-500" />
                  What risks should I focus on?
                </button>
                <button 
                  onClick={() => setInput('How can I optimize my BOM costs?')}
                  className="text-left text-sm p-3 rounded-xl bg-gray-50 hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200"
                >
                  <Sparkles className="h-3 w-3 inline mr-2 text-green-500" />
                  How can I optimize my BOM costs?
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.role === 'user'
                        ? 'bg-green-600 text-white'
                        : msg.role === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex gap-2">
            <Input
              placeholder={t('ai.placeholder', 'Ask about calculations, data, or strategy...')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 bg-white border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-xl px-4"
              data-testid="ai-chat-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {t('ai.pressEnter', 'Press Enter to send')} • {t('ai.contextAware', 'Context-aware AI assistant')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FloatingAIChat;
